import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { t } from "i18next";
import { useFormik } from "formik";
import {
  Breadcrumb,
  DefaultButton,
  PrimaryButton,
  Checkbox,
  TextField,
  ChoiceGroup,
} from "@fluentui/react";
import {
  ACTIONS,
  AuthRoutes,
  TOAST_TYPE,
  USERTYPES,
} from "../../../constants/Constants";
import UserDetailsSection from "../../User/components/UserDetailsSetion/UserDetailsSection";
import { data, setInitialProfileData } from "../../User/helpers/InitialData";
import { AdminSchema } from "../../../Schemas/AdminSchema";
import loaderContext from "../../../context/loader/LoaderContext";
import toastContext from "../../../context/toast/ToastContext";
import MobileInput from "../../../components/MobileInput";
import {
  submitUserData,
  submitUserProfileData,
} from "../../User/helpers/SubmitUserData";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  activeStatusOptions,
  assignmentConfirmationOptions,
  radiologistTimeSpentOptions,
} from "../../User/helpers/CheckboxOptions";
import { choiceGroupStyles } from "../../User/helpers/Styles";
import userContext from "../../../context/users/UserContext";
import { getSignatureImage, userProfile } from "../../../service/UserService";

const ProfileSettings = (props) => {
  const [isRendered, setIsRendered] = useState(false);
  const { roles, authenticated, loadByUsername, username } =
    useContext(userContext);
  const { ref, setLoaderRunning } = useContext(loaderContext);
  const { showToastv2 } = useContext(toastContext);
  const navigate = useNavigate();
  const [expertRadiologist, setExpertRadiologist] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [radiologistSignatureImage, setRadiologistSignatureImage] = useState("");
  const [customMobileError, setCustomMobileError] = useState(null);
  const [radiologistSignatureImageFileName, setRadiologistSignatureImageFileName] = useState("No File Chosen");
  const [isRadiologistSignatureImageDeleted, setIsRadiologistSignatureImageDeleted] = useState(false);

  const handleInputValidationMobile = (inputNumber, country) => {
    let targetLength = country.format.match(/\./g).length;
    let fixedLength = targetLength - country.dialCode.length;
    let inputTargetLength = inputNumber.length - country.dialCode.length;
    // console.log("--- Mobile Field Values ---",inputNumber,targetLength,fixedLength,inputTargetLength,country);
    if (
      inputTargetLength != fixedLength &&
      inputNumber.length > country.dialCode.length &&
      !(inputNumber.length >= 10 && fixedLength > 10)
    ) {
      console.log("--- Error in validation mobile field ---");
      setCustomMobileError(
        "For " + country.name + " (" + fixedLength + " digits required)"
      );
      return;
    }
    if (inputTargetLength == 0 || inputNumber.length < 2) {
      console.log("---- Error mobile field empty ----");
      setTimeout(() => {
        setCustomMobileError("Mobile cannot be blank");
      }, 100);
      return;
    }
    console.log("---- No error in mobile field -----");
    setTimeout(() => {
      setCustomMobileError(null);
    }, 100);
  };

  const handleClearRadiologistSignatureImage = () => {
      
    console.log("In handleClearRadiologistSignatureImage");
    // values.signatureDto.signatureImage = null;
    // values.signatureDto.signatureImageUrl = null;
    setRadiologistSignatureImage("");
    setRadiologistSignatureImageFileName("No File Chosen");
    setIsRadiologistSignatureImageDeleted(true);
    setValues({
      ...values,
      signatureDto: {
        ...values.signatureDto,
        isSignatureImageDeleted: 1,
        signatureImageUrl: null
        
      }
    })
  }

  useEffect(() => {
    if (!authenticated) {
      loadByUsername();
    }
    getUserProfile();
  }, [isRendered, roles]);

  const getUserProfile = async () => {
    let response = await userProfile();
    if (response.status === 200 && response.data.result === "SUCCESS") {
      setProfileData(response.data.data);
      if (
        roles[0] === USERTYPES.RADIOLOGIST &&
        response.data.data.signatureDto.signatureImageUrl !== "" &&
        response.data.data.signatureDto.signatureImageUrl !== null
      ) {
        fetchImage(response.data.data.signatureDto.signatureImageUrl);
      }
    }
  };

  const fetchImage = async (imageName) => {
    let response = await getSignatureImage(imageName);
    let base64Flag = "data:image/jpeg;base64,";
    if (response.status === 200) {
      setRadiologistSignatureImage(base64Flag + response.data.data);
    }
  };

  const initialData = setInitialProfileData(profileData);

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
    resetForm
  } = useFormik({
    initialValues: useMemo(
      () => setInitialProfileData(profileData),
      [profileData]
    ),
    enableReinitialize: true,
    validationSchema: AdminSchema,

    onSubmit: async (values, actions) => {
      if (customMobileError != null && customMobileError != "") {
        actions.setFieldError("mobile", customMobileError);
        return;
      }

      setLoaderRunning(true);
      ref.current.continuousStart();

      try {
        const response = await submitUserProfileData(
          roles[0],
          values,
          profileData
        );
        if (response.status === 200 && response.data.result === "SUCCESS") {
          setLoaderRunning(false);
          ref.current.complete();
          setIsRendered(!isRendered);
          showToastv2(TOAST_TYPE.SUCCESS, "User updated successfully");
        } else {
          if (response.status === 400) {
            setLoaderRunning(false);
            ref.current.complete();
            showToastv2(TOAST_TYPE.ERROR, response?.data?.message);
            return;
          }
          let showGenericError = true;
          response.data.data.forEach((error) => {
            actions.setFieldError(error.fieldName, error.defaultMessage);
            if(error.fieldName === "signatureFile") {
              showToastv2(TOAST_TYPE.ERROR, t("user.signature-invalid-type"));
              showGenericError = false;
            }
          });
          setLoaderRunning(false);
          ref.current.complete();
          if(showGenericError) {
            showToastv2(TOAST_TYPE.ERROR, t("string-literals.error-msg"));
          }
        }
      } catch (e) {
        console.log("--Exception--", e);
        setLoaderRunning(false);
        ref.current.complete();
        showToastv2(TOAST_TYPE.ERROR, t("string-literals.error-msg"));
      }
    },
  });

  const setActive = useCallback((e, option) => {
    values.active = option.key === "true" ? true : false;
  });

  const setShowAssignmentConfirmation = useCallback((e, option) => {
    values.showConfirmation = option.key === "true" ? true : false;
  });

  const setShowRadiologistTime = useCallback((e, option) => {
    values.showRadiologistTime = option.key === "true" ? true : false;
  });

  const setRadiologistType = () => {
    if (profileData.radiologistType === 2) {
      setExpertRadiologist(true);
      values.signatureText = "";
      values.signatureImage = null;
    } else {
      setExpertRadiologist(false);
    }
  };

  useEffect(() => {
    setRadiologistType();
  }, [profileData]);

  const allowedFileTypes = [".JPEG", ".jpeg", ".PNG", ".png", ".JPG", ".jpg"];

  return (
    <div className="tab-section">
      <section className="sectionA">
        <p
          style={{
            fontSize: "20px",
            fontWeight: "600",
            padding: "0px 0px",
            marginBottom: "28px",
          }}
        >
          {t("user.user-details")}
        </p>
      </section>

      {!isRendered && (
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
              <div className="form-heading">{t("user.first-name")}</div>
              <div className="form-mt-10">{profileData.firstName}</div>
            </div>
            <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
              <div className="form-heading">{t("user.last-name")}</div>
              <div className="form-mt-10">{profileData.lastName}</div>
            </div>
          </div>
          <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
            <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
              <div className="form-heading">{t("user.email")}</div>
              <div className="form-mt-10">{profileData.email}</div>
            </div>
            <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
              <div className="form-heading">{t("user.mobile")}</div>
              <div className="form-mt-10">{profileData.mobile}</div>
            </div>
          </div>
          <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
            <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl12">
              <div className="form-heading">{t("user.username")}</div>
              <div className="form-mt-10">{profileData.username}</div>
            </div>
          </div>
          <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
            <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl12">
              {process.env.REACT_APP_ENTERPRISE_PLATFORM === "false" && (
                <div className="form-heading">
                  {roles[0] === USERTYPES.FIRMADMIN
                    ? t("user.firm-code")
                    : t("user.code")}
                </div>
              )}
              {process.env.REACT_APP_ENTERPRISE_PLATFORM === "true" && (
                <div className="form-heading">
                  {roles[0] === USERTYPES.FIRMADMIN
                    ? t("user.hospital-code")
                    : t("user.code")}
                </div>
              )}
              <div className="form-mt-10">
                {roles[0] === USERTYPES.FIRMADMIN
                  ? profileData.firm?.firmCode
                  : profileData.code}
              </div>
            </div>
          </div>
          {(roles[0] === USERTYPES.FIRMDOCTOR ||
            roles[0] === USERTYPES.FIRMTECHNICIAN) && (
            <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
              <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl12">
                <div className="form-heading">
                  {process.env.REACT_APP_ENTERPRISE_PLATFORM === "false"
                    ? t("user.firm")
                    : t("user.hospital")}
                </div>
                <div className="form-mt-10">
                  {profileData.userFirmDto?.name}
                </div>
              </div>
            </div>
          )}
          {roles[0] === USERTYPES.RADIOLOGIST && (
            <>
              <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
                <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl12">
                  <div className="form-heading">{t("user.signature-text")}</div>
                  <div className="form-mt-10">
                    {profileData.signatureDto?.signatureText}
                  </div>
                </div>
              </div>

              <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
                <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl12">
                  <div className="form-heading">
                    {t("user.signature-image")}
                  </div>
                  <div className="form-mt-10">
                    {radiologistSignatureImage != "" ? (
                      <img
                        style={{
                          height: "90px",
                          width: "150px",
                          paddingTop: "10px",
                          paddingBottom: "10px",
                        }}
                        src={radiologistSignatureImage}
                      />
                    ) : (
                      <div>{t("user.no-image-found")}</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="view-page-buttons">
            {process.env.REACT_APP_ENTERPRISE_PLATFORM === "false" && (
              <PrimaryButton
                className="view-page-button"
                text="Edit"
                onClick={() => setIsRendered(!isRendered)}
              />
            )}
            <DefaultButton
              className="view-page-button"
              text="Back"
              onClick={() => navigate(-1)}
            />
          </div>
        </div>
      )}
      {isRendered && (
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <TextField
              className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6"
              name="firstName"
              placeholder={t("user.first-name")}
              label={t("user.first-name")}
              onChange={handleChange}
              value={values.firstName}
              onBlur={handleBlur}
              errorMessage={touched.firstName && errors.firstName}
            />

            <TextField
              className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6"
              name="lastName"
              placeholder={t("user.last-name")}
              label={t("user.last-name")}
              onChange={handleChange}
              value={values.lastName}
              onBlur={handleBlur}
              errorMessage={touched.lastName && errors.lastName}
            />
          </div>
          <div className="ms-Grid-row" style={{ marginTop: "20px" }}>
            <TextField
              className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6"
              name="email"
              placeholder={t("user.email")}
              label={t("user.email")}
              onChange={handleChange}
              value={values.email}
              onBlur={handleBlur}
              errorMessage={touched.email && errors.email}
            />
            <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
              <MobileInput
                name={`mobile`}
                value={values.mobile}
                label={t("user.mobile")}
                onChangeCustom={(value, data) => {
                  handleInputValidationMobile(value, data);
                  values.mobile = "+" + value;
                }}
                touched={touched.mobile}
                //errorMessage={errors.mobile}
              />
            </div>
          </div>
          <div
            className="ms-Grid-row"
            style={{ marginTop: "20px", marginBottom: "20px" }}
          >
            <TextField
              className={"form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl12"}
              name="username"
              placeholder={t("user.username")}
              label={t("user.username")}
              onChange={handleChange}
              value={values.username}
              onBlur={handleBlur}
              errorMessage={touched.username && errors.username}
              readOnly
            />
          </div>
          {(roles[0] === USERTYPES.FIRMADMIN ||
            roles[0] === USERTYPES.SUPERVISOR) && (
            <div className="ms-Grid-row single-line-form-field">
              <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl6">
                <p className="single-line-form-field-key">{t("user.action")}</p>
              </div>
              <div className="ms-Grid-col ms-sm12 ms-lg8 ms-xl6 single-line-form-field-value">
                <div>
                  <ChoiceGroup
                    defaultSelectedKey={values.active ? "true" : "false"}
                    options={activeStatusOptions}
                    styles={choiceGroupStyles}
                    onChange={setActive}
                  />
                </div>
              </div>
            </div>
          )}

          {(roles[0] === USERTYPES.ADMIN ||
            roles[0] === USERTYPES.FIRMADMIN ||
            roles[0] === USERTYPES.SUPERVISOR) && (
            <div className="ms-Grid-row single-line-form-field">
              <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl6">
                <p className="single-line-form-field-key">
                  {t("user.show-assignment-confirmation")}
                </p>
              </div>
              <div className="ms-Grid-col ms-sm12 ms-lg8 ms-xl6 single-line-form-field-value">
                <div>
                  <ChoiceGroup
                    defaultSelectedKey={
                      values.showConfirmation ? "true" : "false"
                    }
                    options={assignmentConfirmationOptions}
                    styles={choiceGroupStyles}
                    onChange={setShowAssignmentConfirmation}
                  />
                </div>
              </div>
            </div>
          )}

          {roles[0] === USERTYPES.SUPERVISOR && (
            <div className="ms-Grid-row single-line-form-field">
              <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl6">
                <p className="single-line-form-field-key">
                  {t("user.view-radiologist-time-spent")}
                </p>
              </div>
              <div className="ms-Grid-col ms-sm12 ms-lg8 ms-xl6 single-line-form-field-value">
                <div>
                  <ChoiceGroup
                    defaultSelectedKey={
                      values.showRadiologistTime ? "true" : "false"
                    }
                    options={radiologistTimeSpentOptions}
                    styles={choiceGroupStyles}
                    onChange={setShowRadiologistTime}
                  />
                </div>
              </div>
            </div>
          )}

          {roles[0] === USERTYPES.RADIOLOGIST && (
            <>
              <div className="ms-Grid-row single-line-form-field">
                <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl6">
                  <p className="single-line-form-field-key">
                    {t("user.send-study-assign-mail")}
                  </p>
                </div>
                <div className="ms-Grid-col ms-sm12 ms-lg8 ms-xl6 single-line-form-field-value">
                  <div>
                    <Checkbox
                      defaultChecked={values.sendAssignMail}
                      value={values.sendAssignMail}
                      styles={choiceGroupStyles}
                      onChange={() => {
                        values.sendAssignMail = !values.sendAssignMail;
                      }}
                    />
                  </div>
                </div>
              </div>

              {!expertRadiologist && (
                <>
                  {/* signatue text */}
                  <div className="ms-Grid-row single-line-form-field-start">
                    <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl6">
                      <p className="single-line-form-field-key">
                        {t("user.signature-text")}
                      </p>
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6 single-line-form-field-value">
                      <div>
                        <TextField
                          multiline
                          rows={3}
                          name="signatureText"
                          placeholder={t("user.signature-text")}
                          onChange={handleChange}
                          value={values.signatureText}
                          onBlur={handleBlur}
                          errorMessage={
                            touched.signatureText && errors.signatureText
                          }
                        />
                      </div>
                    </div>
                  </div>
                  {/* end of signature text */}

                  {/* signature image */}
                  <div
                    className="ms-Grid-row single-line-form-field-start"
                    style={{ marginTop: "10px" }}
                  >
                    <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl6">
                      <p className="single-line-form-field-key">
                        {t("user.signature-image")}
                      </p>
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg8 ms-xl6 single-line-form-field-value">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {radiologistSignatureImage != "" ? (
                          <img
                            style={{
                              height: "90px",
                              width: "150px",
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            }}
                            src={radiologistSignatureImage}
                          />
                        ) : (
                          <div></div>
                        )}
                        <input
                           style={{ width: "112px", overflow: "hidden" }}
                          type="file"
                          id="signatureFile"
                          onChange={(event) => {
                            values.signatureImage = event.target.files;
                            setRadiologistSignatureImageFileName(event.target.files[0].name);
                            values.signatureDto.isSignatureImageDeleted = 0;
                            setIsRadiologistSignatureImageDeleted(false);

                          }}
                          className="file-input-container"
                          accept={allowedFileTypes.join(",")}
                        />
                         <label style={{ marginLeft: "10px" }} for="signatureFile">
                        {radiologistSignatureImageFileName}
                          </label>
                          {(((radiologistSignatureImage != null && radiologistSignatureImage != "") || (radiologistSignatureImageFileName != null && radiologistSignatureImageFileName != "" && radiologistSignatureImageFileName != "No File Chosen")) && (values.signatureDto.isSignatureImageDeleted == 0)) &&
                            (<PrimaryButton
                      text="Clear Signature Image"
                            className="study-list-clear-all-button"
                            style={{ marginLeft: "5px", marginTop: "5px"}} 
                      onClick={() => {
                        handleClearRadiologistSignatureImage()
                        // setRadiologistSignatureImage(false);
                        document.getElementById('signatureFile').value = '';
                      }
                    }
                            />) }
                             
                                  
                        </div>
                        <p
                          style={{
                            fontSize: "12px",
                            fontStyle: "italic",
                          }}
                        >
                          {t("user.signature-image-size-msg")}
                        </p>
                       
                      </div>
                  </div>
                  {/* end of signature image */}
                </>
              )}
            </>
          )}
          <div className="view-page-buttons">
            <PrimaryButton
              className="view-page-button"
              text="Save"
              onClick={handleSubmit}
            />
            <DefaultButton
              className="view-page-button"
              text="Cancel"
              onClick={() => {
                setIsRendered(false);
                resetForm();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
